import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { Link } from 'react-router-dom';
import { 
  Users, 
  ArrowRight, 
  ArrowLeft, 
  DollarSign, 
  Clock,
  Plus,
  Building,
  Settings
} from 'lucide-react';
import { referralService } from '../api/referrals';
import { commissionService } from '../api/commissions';
import { useStore } from '../../../lib/store';
import { isDoctor } from '../../../lib/rbac';
import type { Referral, CommissionEntry } from '../types';

export default function ReferralOverview() {
  const { formatCurrency } = useCurrency();
  const { user } = useStore();
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [commissions, setCommissions] = useState<CommissionEntry[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [referralsData, commissionsData] = await Promise.all([
        referralService.getAll(),
        commissionService.getAllEntries(),
      ]);
      setReferrals(referralsData);
      setCommissions(commissionsData);
    } catch (error) {
      console.error('Failed to load referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    totalReferrals: referrals.length,
    inReferrals: referrals.filter(r => r.type === 'in').length,
    outReferrals: referrals.filter(r => r.type === 'out').length,
    pendingReferrals: referrals.filter(r => r.status === 'pending').length,
    completedReferrals: referrals.filter(r => r.status === 'completed').length,
    unpaidCommissions: commissions.filter(c => c.payoutStatus === 'unpaid').length,
    totalUnpaidAmount: commissions
      .filter(c => c.payoutStatus === 'unpaid')
      .reduce((sum, c) => sum + c.calculatedAmount, 0),
  };

  const quickLinks = [
    {
      title: 'New Referral',
      description: 'Create a new referral',
      href: '/referrals/new',
      icon: Plus,
      color: 'bg-blue-500',
    },
    {
      title: 'In Referrals',
      description: 'Manage incoming referrals',
      href: '/referrals/in',
      icon: ArrowRight,
      color: 'bg-green-500',
    },
    {
      title: 'Out Referrals',
      description: 'Manage outgoing referrals',
      href: '/referrals/out',
      icon: ArrowLeft,
      color: 'bg-orange-500',
    },
    {
      title: 'Partners',
      description: 'Manage referral partners',
      href: '/referrals/partners',
      icon: Building,
      color: 'bg-purple-500',
    },
    {
      title: 'Commissions',
      description: 'Track commissions & payouts',
      href: '/referrals/commissions',
      icon: DollarSign,
      color: 'bg-emerald-500',
    },
    {
      title: 'Policies',
      description: 'Configure referral policies',
      href: '/referrals/policies',
      icon: Settings,
      color: 'bg-gray-500',
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Referral System</h1>
        <Link
          to="/referrals/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>New Referral</span>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Referrals</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalReferrals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <ArrowRight className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Referrals</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inReferrals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingReferrals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Unpaid Commissions</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalUnpaidAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {quickLinks
          .filter(link => {
            if (isDoctor(user)) {
              return !['Partners', 'Commissions', 'Policies'].includes(link.title);
            }
            return true;
          })
          .map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                to={link.href}
                className="bg-white p-6 rounded-lg shadow border hover:shadow-md transition-shadow"
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-lg ${link.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{link.title}</h3>
                    <p className="text-sm text-gray-600">{link.description}</p>
                  </div>
                </div>
              </Link>
            );
          })}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Referrals</h2>
        </div>
        <div className="p-6">
          {referrals.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No referrals yet</p>
              <Link
                to="/referrals/new"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Create your first referral
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {referrals.slice(0, 5).map((referral) => (
                <div key={referral.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg ${
                      referral.type === 'in' ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                      {referral.type === 'in' ? (
                        <ArrowRight className={`w-4 h-4 ${
                          referral.type === 'in' ? 'text-green-600' : 'text-orange-600'
                        }`} />
                      ) : (
                        <ArrowLeft className="w-4 h-4 text-orange-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {referral.patientName || `Patient ${referral.patientId}`}
                      </p>
                      <p className="text-sm text-gray-500">{referral.reason}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      referral.status === 'completed' ? 'bg-green-100 text-green-800' :
                      referral.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      referral.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {referral.status}
                    </span>
                    <Link
                      to={`/referrals/${referral.id}`}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
